import React, { useState } from 'react';
import { Mail } from 'react-feather';
import { Button } from 'components/quashed';
import { trackEvent, TRACKING_EVENTS, validateEmail } from 'helpers';
import { sendEmailValidationCode, validateEmailCode } from 'services';
import QuestionPage from './QuestionPage';
import styles from '../styles/VerificationPage.module.scss';

const CodeVerification = ({
  employer,
  removeButton,
  values,
  setState,
  onSubmit,
}) => {
  const questions = [
    {
      key: 'vcode',
      placeholder: 'Please enter your verification code',
      labelAfter: (
        <>
          <Mail size={14} />
          Please check your spam folder if you do not receive the code.
        </>
      ),
      required: true,
      showButton: false,
      children: removeButton,
      inputImg: employer?.smallLogoUrl,
    },
  ];

  const handleSubmit = async (data) => {
    try {
      setState(R.assoc('loading', true));
      const res = await validateEmailCode(values.email, data.vcode);
      onSubmit(res.data.user);
      setState(
        R.mergeLeft({
          loading: false,
          error: '',
        }),
      );
    } catch (e) {
      const errMsg =
        'Verification unsuccessful, please try again or contact us.';
      trackEvent(TRACKING_EVENTS.Q4EEmailVerificationError, {
        error: e.response?.data?.errorMessage || e.message,
      });
      setState(
        R.mergeLeft({
          error: errMsg,
          loading: false,
        }),
      );
    }
  };

  const handleBack = () => {
    setState(R.assoc('showCode', false));
  };

  return (
    <QuestionPage
      hideBack={false}
      title={`We’ve sent a code to ${values.email}`}
      error={values.error}
      loading={values.loading}
      questions={questions}
      submitText={
        values.loading ? 'Verifying' : values.error ? 'Try again' : 'Next'
      }
      values={values}
      onBack={handleBack}
      onSubmit={handleSubmit}
    />
  );
};

const EmailVerification = ({
  employer,
  removeButton,
  values,
  onBack,
  setState,
}) => {
  const questions = [
    {
      key: 'email',
      placeholder: 'Enter work email',
      labelAfter: (
        <>
          <Mail size={14} />
          We will send a verification code to your work email.
        </>
      ),
      required: true,
      showButton: false,
      children: removeButton,
      inputImg: employer?.smallLogoUrl,
    },
  ];

  const handleSubmit = async ({ email }) => {
    if (validateEmail(email)) {
      try {
        setState(R.assoc('loading', true));
        await sendEmailValidationCode(email);
        setState(
          R.mergeLeft({
            email,
            showCode: true,
            loading: false,
            error: '',
          }),
        );
      } catch (e) {
        const errMsg = e.response.data.errorMessage;
        trackEvent(TRACKING_EVENTS.Q4EEmailConfirmError, { error: errMsg });
        setState(
          R.mergeLeft({
            error: errMsg,
            loading: false,
          }),
        );
      }
    } else {
      const errMsg = 'Email is not valid, please try again.';
      trackEvent(TRACKING_EVENTS.Q4EEmailConfirmError, { error: errMsg });
      setState(R.assoc('error', errMsg));
    }
  };

  return (
    <QuestionPage
      hideBack={false}
      error={values.error}
      loading={values.loading}
      title="Please confirm your work email"
      questions={questions}
      values={values}
      onBack={onBack}
      onSubmit={handleSubmit}
    />
  );
};

const VerificationPage = ({
  employer,
  onBack,
  onRemoveWorkplace,
  onSubmit,
  resetActive,
}) => {
  const [state, setState] = useState({
    error: '',
    loading: false,
    email: '',
    showCode: false,
    vcode: '',
  });

  const handleRemoveWorkplace = () => {
    trackEvent(TRACKING_EVENTS.Q4ENotEmployeeClick);
    onRemoveWorkplace();
    resetActive();
  };

  const removeButton = (
    <Button
      className={styles.remove}
      inverted={true}
      text={`I’m not a ${employer.name} employee`}
      onClick={handleRemoveWorkplace}
      type="button"
    />
  );

  const PageComponent = state.showCode ? CodeVerification : EmailVerification;

  return (
    <PageComponent
      employer={employer}
      removeButton={removeButton}
      values={state}
      onBack={onBack}
      setState={setState}
      onSubmit={onSubmit}
    />
  );
};

export default VerificationPage;
